"use client";

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CustomerPricingService, ServicePreviewItem, PricingItem } from "../services/customer-pricing-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import EmptyState from "../../../components/custom/empty-state";
import { ApiConfig, API_CUSTOMER_ROUTER } from "../../../services/api-config";
import { ALL_BRANCHES_VALUE } from "../../../hooks/use-catalog-branch-options";
import { formatCurrency } from "../../../utils/helpers";

// ============================================
// TYPES
// ============================================

/**
 * Option cho dropdown khách hàng (tìm-kiếm + phân trang qua API, không tải cả ~200 khách vào bộ nhớ
 * như trước). `[key: string]: any` để giữ nguyên các field khác BE trả về dù không khai báo hết ở đây.
 */
interface CustomerOption {
  id: number;
  name?: string;
  phone?: string;
  default_branch_id?: number;
  [key: string]: any;
}

interface Branch {
  id: number;
  name: string;
}

interface CustomerPricingModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** Không còn dùng — giá riêng áp dụng cho mọi chi nhánh (branch_id = 0). Giữ để không phải sửa nơi gọi. */
  branches?: Branch[];
}

// ============================================
// HELPERS
// ============================================

/** Bỏ dấu + chữ thường để tìm "chan ga" ra "Chăn - Ga". */
const normalizeText = (s: string): string =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

const DIFFERENCE_TYPE = {
  PERCENT: 1,
  FIXED: 2,
};

/** Số gợi ý tối đa hiển thị một lần trong ô tìm dịch vụ. */
const MAX_SUGGESTIONS = 20;

// ============================================
// COMPONENT
// ============================================

/**
 * Giá riêng khách hàng — DÙNG CHUNG mọi chi nhánh (branch_id = 0, cùng quy ước với bảng giá dùng chung).
 *
 * Luồng: chọn khách → bảng hiện các dịch vụ khách ĐÃ có giá riêng → tìm dịch vụ trong bảng giá để thêm
 * xuống bảng → sửa giá → lưu từng dòng hoặc lưu tất cả. Danh sách gợi ý lấy từ API preview (toàn bộ
 * bảng giá dùng chung, ~vài chục dòng) và lọc ngay trên trình duyệt.
 */
const CustomerPricingModal: React.FC<CustomerPricingModalProps> = ({ open, onClose, onSaved }) => {
  const branchId = ALL_BRANCHES_VALUE;

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const customerId = selectedCustomer?.id ?? 0;
  const [differenceType, setDifferenceType] = useState<number>(DIFFERENCE_TYPE.PERCENT);
  const [differenceValue, setDifferenceValue] = useState<string>("");
  const [isDecrease, setIsDecrease] = useState<boolean>(true); // true = giảm giá, false = tăng giá

  // Toàn bộ bảng giá dùng chung (nguồn gợi ý) + các dòng đang có trong bảng
  const [catalog, setCatalog] = useState<ServicePreviewItem[]>([]);
  const [services, setServices] = useState<ServicePreviewItem[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Ô tìm dịch vụ
  const [keyword, setKeyword] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Saving state
  const [savingAll, setSavingAll] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // API hooks
  const { fetch: previewPricing, loading: previewing } = CustomerPricingService.preview();
  const { fetch: bulkSave } = CustomerPricingService.bulkSave();
  const { fetch: singleSave } = CustomerPricingService.singleSave();
  const { fetch: deletePricing } = CustomerPricingService.delete();

  // Store refs to prevent infinite loops
  const previewPricingRef = useRef(previewPricing);
  useEffect(() => {
    previewPricingRef.current = previewPricing;
  }, [previewPricing]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedCustomer(null);
      setDifferenceType(DIFFERENCE_TYPE.PERCENT);
      setDifferenceValue("");
      setIsDecrease(true);
      setCatalog([]);
      setServices([]);
      setKeyword("");
      setShowSuggestions(false);
    }
  }, [open]);

  /**
   * Tải bảng giá dùng chung kèm giá riêng hiện có của khách.
   * Bảng chỉ giữ các dòng khách ĐÃ có giá riêng; phần còn lại làm nguồn gợi ý cho ô tìm dịch vụ.
   */
  const loadServices = useCallback(async () => {
    if (!customerId) return;

    setLoadingServices(true);
    try {
      const res = await previewPricingRef.current({
        customer_id: customerId,
        branch_id: branchId,
      });

      if (res && isSuccess(res) && res.data?.services) {
        // Tên hiển thị "[Mã sản phẩm] - [Sản phẩm]" (bỏ tên nhóm dịch vụ) — cũng là chuỗi ô tìm dịch vụ so khớp,
        // nên tìm được theo cả mã lẫn tên sản phẩm. Thiếu tên sản phẩm thì giữ cách hiển thị cũ.
        const mapped: ServicePreviewItem[] = res.data.services.map((s: ServicePreviewItem) => ({
          ...s,
          service_name: s.service_type_name
            ? [s.service_type_code, s.service_type_name].filter(Boolean).join(" - ")
            : [s.service_name, s.service_type_name].filter(Boolean).join(" - ") || `Dịch vụ #${s.service_type_map_id}`,
        }));
        setCatalog(mapped);
        setServices(mapped.filter((s) => s.is_saved));
      } else {
        setCatalog([]);
        setServices([]);
        showToast({ message: res?.message || "Không thể tải danh sách dịch vụ", type: "error" });
      }
    } catch (error) {
      console.error("Error loading services:", error);
      showToast({ message: "Không thể tải danh sách dịch vụ", type: "error" });
    } finally {
      setLoadingServices(false);
    }
  }, [customerId, branchId]);

  // Đổi khách → tải lại
  useEffect(() => {
    setCatalog([]);
    setServices([]);
    setKeyword("");
    if (customerId) void loadServices();
  }, [customerId, loadServices]);

  // Gợi ý: dịch vụ chưa có trong bảng, khớp từ khoá (không dấu)
  const suggestions = useMemo(() => {
    const inTable = new Set(services.map((s) => s.service_type_map_id));
    const q = normalizeText(keyword.trim());
    return catalog
      .filter((s) => !inTable.has(s.service_type_map_id))
      .filter((s) => !q || normalizeText(s.service_name).includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [catalog, services, keyword]);

  // Thêm dịch vụ xuống bảng — giá riêng mặc định = giá gốc để người dùng sửa
  const handleAddService = (item: ServicePreviewItem) => {
    const original = Number(item.original_price) || 0;
    setServices((prev) => [...prev, { ...item, custom_price: original, calculated_price: original, is_saved: false }]);
    setKeyword("");
    setShowSuggestions(false);
  };

  // Apply difference to all services in table
  const handleApplyDifference = () => {
    if (!customerId || !differenceValue || services.length === 0) return;

    const rawVal = parseFloat(differenceValue);
    if (isNaN(rawVal) || rawVal < 0) {
      showToast({ message: "Giá trị chênh lệch không hợp lệ", type: "error" });
      return;
    }

    // Apply sign based on increase/decrease selection
    const diffVal = isDecrease ? -rawVal : rawVal;

    setServices((prev) =>
      prev.map((s) => {
        const origPrice = Number(s.original_price) || 0;
        const newPrice =
          differenceType === DIFFERENCE_TYPE.PERCENT ? origPrice * (1 + diffVal / 100) : origPrice + diffVal;
        const safePrice = isNaN(newPrice) ? origPrice : Math.round(Math.max(0, newPrice));
        return { ...s, custom_price: safePrice, calculated_price: safePrice, is_saved: false };
      })
    );

    showToast({ message: "Đã áp dụng chênh lệch", type: "success" });
  };

  // Update single service price — sửa xong thì dòng về "Chưa lưu"
  const handlePriceChange = (serviceId: number, value: string) => {
    const price = parseFloat(value) || 0;
    setServices((prev) =>
      prev.map((s) =>
        s.service_type_map_id === serviceId ? { ...s, custom_price: Math.max(0, price), is_saved: false } : s
      )
    );
  };

  // Save all prices
  const handleSaveAll = async () => {
    if (!customerId || services.length === 0) return;

    setSavingAll(true);
    try {
      const rawVal = differenceValue ? parseFloat(differenceValue) : undefined;
      const signedVal = rawVal !== undefined ? (isDecrease ? -rawVal : rawVal) : undefined;

      const pricings: PricingItem[] = services.map((s) => ({
        service_type_map_id: Number(s.service_type_map_id),
        original_price: Number(s.original_price),
        custom_price: Number(s.custom_price),
        difference_type: differenceValue ? differenceType : undefined,
        difference_value: signedVal,
      }));

      const res = await bulkSave({
        customer_id: Number(customerId),
        branch_id: branchId,
        pricings,
      });

      if (res && isSuccess(res)) {
        showToast({ message: `Đã lưu ${res.data?.saved_count || services.length} giá`, type: "success" });
        // Tải lại để có id của các dòng vừa tạo (cần cho nút xoá)
        await loadServices();
        onSaved?.();
      } else {
        showToast({ message: res?.message || "Không thể lưu", type: "error" });
      }
    } catch (error) {
      showToast({ message: "Có lỗi khi lưu", type: "error" });
    } finally {
      setSavingAll(false);
    }
  };

  // Save single price
  const handleSaveSingle = async (service: ServicePreviewItem) => {
    if (!customerId) return;

    setSavingId(service.service_type_map_id);
    try {
      const rawVal = differenceValue ? parseFloat(differenceValue) : undefined;
      const signedVal = rawVal !== undefined ? (isDecrease ? -rawVal : rawVal) : undefined;

      const res = await singleSave({
        customer_id: Number(customerId),
        branch_id: branchId,
        service_type_map_id: Number(service.service_type_map_id),
        original_price: Number(service.original_price),
        custom_price: Number(service.custom_price),
        difference_type: differenceValue ? differenceType : undefined,
        difference_value: signedVal,
      });

      if (res && isSuccess(res)) {
        showToast({ message: "Đã lưu", type: "success" });
        setServices((prev) =>
          prev.map((s) =>
            s.service_type_map_id === service.service_type_map_id
              ? {
                  ...s,
                  is_saved: true,
                  // Kiểu trong customer-pricing-service bị bọc BaseResponse 2 lần; lúc chạy res.data là { id, is_new }
                  existing_id: (res.data as unknown as { id?: number } | undefined)?.id ?? s.existing_id,
                }
              : s
          )
        );
        onSaved?.();
      } else {
        showToast({ message: res?.message || "Không thể lưu", type: "error" });
      }
    } catch (error) {
      showToast({ message: "Có lỗi khi lưu", type: "error" });
    } finally {
      setSavingId(null);
    }
  };

  /**
   * Bỏ 1 dòng khỏi bảng:
   * - Chưa từng lưu → bỏ khỏi bảng, không gọi API.
   * - Đã có giá riêng trong hệ thống → hỏi xác nhận rồi xoá (BE xoá mềm, status = 0; thêm lại sau vẫn được).
   */
  const handleRemove = async (service: ServicePreviewItem) => {
    const removeLocal = () =>
      setServices((prev) => prev.filter((s) => s.service_type_map_id !== service.service_type_map_id));

    if (!service.existing_id) {
      removeLocal();
      return;
    }

    if (!window.confirm(`Xoá giá riêng "${service.service_name}" của khách hàng này?`)) return;

    setDeletingId(service.service_type_map_id);
    try {
      const res = await deletePricing(service.existing_id);
      if (res && isSuccess(res)) {
        showToast({ message: "Đã xoá giá riêng", type: "success" });
        removeLocal();
        // Dòng đã xoá quay về nguồn gợi ý như một dịch vụ chưa có giá riêng
        setCatalog((prev) =>
          prev.map((s) =>
            s.service_type_map_id === service.service_type_map_id ? { ...s, is_saved: false, existing_id: null } : s
          )
        );
        onSaved?.();
      } else {
        showToast({ message: res?.message || "Không thể xoá", type: "error" });
      }
    } catch (error) {
      showToast({ message: "Có lỗi khi xoá", type: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  // Stats
  const savedCount = services.filter((s) => s.is_saved).length;
  const unsavedCount = services.length - savedCount;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[900px] max-w-[95vw] max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Đơn giá riêng khách hàng</h3>
            <p className="text-sm text-slate-500">Thiết lập giá riêng cho khách hàng — áp dụng cho mọi chi nhánh</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-2xl leading-none">
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Customer + tìm dịch vụ */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Khách hàng <span className="text-red-500">*</span>
              </label>
              <SelectDropdownAsyncPaginate<CustomerOption>
                url={API_CUSTOMER_ROUTER.API_LIST}
                projectId={ApiConfig.PROJECT_ID.CUSTOMER_SERVICE}
                limit={20}
                placeholder="-- Chọn khách hàng --"
                labelOption="name"
                classNames="w-full"
                defaultOptions={selectedCustomer ? [selectedCustomer] : []}
                onChange={(selected) => setSelectedCustomer((selected[0] as CustomerOption) ?? null)}
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1">Thêm dịch vụ</label>
              <input
                type="search"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setShowSuggestions(false)}
                disabled={!customerId || loadingServices}
                placeholder={customerId ? "Tìm sản phẩm / dịch vụ để thêm..." : "Chọn khách hàng trước"}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
              />
              {showSuggestions && customerId > 0 && (
                <div className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {suggestions.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-slate-400">
                      {catalog.length === 0 ? "Bảng giá chưa có dịch vụ nào" : "Không tìm thấy dịch vụ phù hợp"}
                    </div>
                  ) : (
                    suggestions.map((item) => (
                      <button
                        key={item.service_type_map_id}
                        type="button"
                        // onMouseDown thay onClick: chạy trước onBlur của ô tìm kiếm, nếu không danh sách đóng trước khi chọn
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleAddService(item);
                        }}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-blue-50"
                      >
                        <span className="text-slate-700">{item.service_name}</span>
                        <span className="shrink-0 text-slate-500">{formatCurrency(item.original_price)}đ</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Difference Input */}
          {customerId > 0 && services.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6 border">
              <div className="text-sm font-medium text-slate-700 mb-3">Áp dụng chênh lệch cho các dịch vụ trong bảng</div>
              <div className="flex flex-wrap items-center gap-4">
                {/* Increase/Decrease buttons */}
                <div className="flex rounded-lg overflow-hidden border border-slate-300">
                  <button
                    type="button"
                    onClick={() => setIsDecrease(true)}
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                      isDecrease ? "bg-green-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    Giảm giá
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDecrease(false)}
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                      !isDecrease ? "bg-red-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    Tăng giá
                  </button>
                </div>

                {/* Difference type */}
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="diffType"
                      checked={differenceType === DIFFERENCE_TYPE.PERCENT}
                      onChange={() => setDifferenceType(DIFFERENCE_TYPE.PERCENT)}
                      className="text-blue-600"
                    />
                    <span className="text-sm">%</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer ml-2">
                    <input
                      type="radio"
                      name="diffType"
                      checked={differenceType === DIFFERENCE_TYPE.FIXED}
                      onChange={() => setDifferenceType(DIFFERENCE_TYPE.FIXED)}
                      className="text-blue-600"
                    />
                    <span className="text-sm">VNĐ</span>
                  </label>
                </div>

                {/* Value input */}
                <input
                  type="number"
                  min="0"
                  value={differenceValue}
                  onChange={(e) => setDifferenceValue(e.target.value)}
                  placeholder={differenceType === DIFFERENCE_TYPE.PERCENT ? "VD: 10" : "VD: 5000"}
                  className="w-28 border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <button
                  onClick={handleApplyDifference}
                  disabled={!differenceValue || previewing}
                  className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Áp dụng
                </button>
              </div>
            </div>
          )}

          {/* Services Table */}
          {!customerId ? (
            <EmptyState message="Vui lòng chọn khách hàng" description="Chọn khách hàng để xem và thiết lập giá riêng." />
          ) : loadingServices ? (
            <div className="text-center py-8 text-slate-500">Đang tải danh sách dịch vụ...</div>
          ) : services.length === 0 ? (
            <EmptyState
              message="Khách hàng chưa có giá riêng"
              description="Tìm dịch vụ ở ô “Thêm dịch vụ” để thêm vào bảng và đặt giá riêng."
            />
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Dịch vụ</th>
                    <th className="px-4 py-2 text-right w-32">Giá gốc</th>
                    <th className="px-4 py-2 text-center w-40">Giá riêng</th>
                    <th className="px-4 py-2 text-right w-32">Chênh lệch</th>
                    <th className="px-4 py-2 text-center w-24">Trạng thái</th>
                    <th className="px-4 py-2 text-center w-28"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {services.map((service, index) => {
                    const safeCustom = Number(service.custom_price) || 0;
                    const safeOriginal = Number(service.original_price) || 0;
                    const diff = safeCustom - safeOriginal;
                    const diffPercent = safeOriginal > 0 ? ((diff / safeOriginal) * 100).toFixed(1) : "0";

                    return (
                      <tr key={service.service_type_map_id} className="hover:bg-slate-50">
                        <td className="px-4 py-2 text-slate-500">{index + 1}</td>
                        <td className="px-4 py-2 font-medium">{service.service_name}</td>
                        <td className="px-4 py-2 text-right text-slate-600">{formatCurrency(service.original_price)}đ</td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={service.custom_price}
                            onChange={(e) => handlePriceChange(service.service_type_map_id, e.target.value)}
                            className="w-full border rounded px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </td>
                        <td
                          className={`px-4 py-2 text-right ${
                            diff < 0 ? "text-green-600" : diff > 0 ? "text-red-600" : "text-slate-500"
                          }`}
                        >
                          {diff !== 0 && (
                            <>
                              {diff > 0 ? "+" : ""}
                              {formatCurrency(diff)}đ
                              <span className="text-xs ml-1">
                                ({diff > 0 ? "+" : ""}
                                {diffPercent}%)
                              </span>
                            </>
                          )}
                          {diff === 0 && <span className="text-slate-400">-</span>}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {service.is_saved ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                              Đã lưu
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
                              Chưa lưu
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          <button
                            onClick={() => handleSaveSingle(service)}
                            disabled={savingId === service.service_type_map_id}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {savingId === service.service_type_map_id ? "..." : "Lưu"}
                          </button>
                          <button
                            onClick={() => handleRemove(service)}
                            disabled={deletingId === service.service_type_map_id}
                            title={service.existing_id ? "Xoá giá riêng" : "Bỏ khỏi bảng"}
                            aria-label={service.existing_id ? "Xoá giá riêng" : "Bỏ khỏi bảng"}
                            className="ml-2 px-2 py-1 text-xs bg-rose-500 text-white rounded hover:bg-rose-600 disabled:opacity-50"
                          >
                            {deletingId === service.service_type_map_id ? "..." : "✕"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {services.length > 0 && (
              <>
                Tổng: {services.length} dịch vụ |<span className="text-green-600 ml-1">Đã lưu: {savedCount}</span> |
                <span className="text-yellow-600 ml-1">Chưa lưu: {unsavedCount}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100">
              Đóng
            </button>
            {services.length > 0 && (
              <button
                onClick={handleSaveAll}
                disabled={savingAll || services.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {savingAll ? "Đang lưu..." : `Lưu tất cả (${services.length})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPricingModal;
